import {
  ProgramStaffClient,
  UserStaffProgramRole,
} from "@clients/programStaffClient"
import { ProgramClassClient } from "@clients/programClassClient"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Popover,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material"
import appIcons from "@utils/appIcons"
import { useEffect, useState } from "react"
import { generateLinkForUser, viewAsUser } from "./accountManagement"

// Class names are tiny and immutable enough to cache for the page's lifetime —
// the popover resolves each assigned class once, across all opened employees.
const classNameCache = new Map<string, Promise<string>>()
function resolveClassName(classId: string): Promise<string> {
  const cached = classNameCache.get(classId)
  if (cached) return cached
  const promise = ProgramClassClient.getClassById(classId)
    .then((programClass) => programClass.name)
    .catch(() => "Unknown class")
  classNameCache.set(classId, promise)
  return promise
}

/**
 * The "why does this person see these calendars" card: their instructor
 * programs with the assigned classes that decide the calendar list, plus
 * one-click verification (view as) and the copy-link fallback.
 */
export function CalendarAssignmentsPopover(props: {
  anchorEl: HTMLElement
  onClose: () => void
  userClientId: string
  name: string
  email: string
}) {
  const { anchorEl, onClose, userClientId, name, email } = props
  const [staffRoles, setStaffRoles] = useState<
    UserStaffProgramRole[] | "loading" | "failed"
  >("loading")
  const [classNames, setClassNames] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    let cancelled = false
    ProgramStaffClient.listUserRoles(userClientId)
      .then((roles) => {
        if (cancelled) return
        setStaffRoles(roles)
        const classIds = roles
          .filter((role) => role.roles.includes("instructor"))
          .flatMap((role) => role.assignedClasses)
        classIds.forEach((classId) => {
          resolveClassName(classId).then((className) => {
            if (cancelled) return
            setClassNames((current) => {
              const next = new Map(current)
              next.set(classId, className)
              return next
            })
          })
        })
      })
      .catch(() => {
        if (!cancelled) setStaffRoles("failed")
      })
    return () => {
      cancelled = true
    }
  }, [userClientId])

  const instructorPrograms =
    staffRoles === "loading" || staffRoles === "failed"
      ? []
      : staffRoles.filter((role) => role.roles.includes("instructor"))
  const totalClasses = instructorPrograms.reduce(
    (sum, program) => sum + program.assignedClasses.length,
    0,
  )

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Stack sx={{ width: 380, p: 2, pb: 1.5 }} gap={1}>
        <Typography variant="body1_sb">{name}&apos;s calendars</Typography>
        <Typography variant="body3" color="text.secondary">
          Personal calendar, plus one calendar per class assigned on each
          program&apos;s Staff page.
        </Typography>

        {staffRoles === "loading" && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
          </Stack>
        )}
        {staffRoles === "failed" && (
          <Typography variant="body2" color="error">
            Could not load staff assignments.
          </Typography>
        )}

        {staffRoles !== "loading" && staffRoles !== "failed" && (
          <>
            <Chip
              size="small"
              variant="outlined"
              color={totalClasses === 0 ? "warning" : "default"}
              icon={
                totalClasses === 0 ? (
                  <SvgIcon
                    component={appIcons.alertTriangle}
                    inheritViewBox
                    sx={{ fill: "none", width: 14, height: 14 }}
                  />
                ) : undefined
              }
              label={
                totalClasses === 0
                  ? "Personal calendar only — no classes assigned"
                  : `Personal + ${totalClasses} class calendar${
                      totalClasses === 1 ? "" : "s"
                    }`
              }
              sx={{ width: "fit-content" }}
            />
            {instructorPrograms.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Not staffed as an instructor on any program.
              </Typography>
            )}
            <Stack gap={1} sx={{ maxHeight: 320, overflowY: "auto" }}>
              {instructorPrograms.map((program) => (
                <Box key={program.programId}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    gap={1}
                  >
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {program.programDisplayName}
                    </Typography>
                    <Link
                      href={`/program/${program.programId}?tab=staff`}
                      target="_blank"
                      variant="body3"
                      sx={{ flexShrink: 0 }}
                    >
                      Open staff page
                    </Link>
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      mt: 0.5,
                    }}
                  >
                    {program.assignedClasses.length === 0 && (
                      <Typography variant="body3" color="warning.main">
                        No classes assigned in this program
                      </Typography>
                    )}
                    {program.assignedClasses.map((classId) => (
                      <Chip
                        key={classId}
                        size="small"
                        variant="outlined"
                        label={classNames.get(classId) ?? "…"}
                        sx={{ maxWidth: 170 }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          </>
        )}

        <Divider />
        <Stack direction="row" gap={1} sx={{ flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => viewAsUser(email, "campus")}
          >
            View as · Campus
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => viewAsUser(email, "lms")}
          >
            View as · LMS
          </Button>
          <Button size="small" onClick={() => generateLinkForUser(email)}>
            Copy link
          </Button>
        </Stack>
      </Stack>
    </Popover>
  )
}
